/**
 * Shared layout helpers for ACR HTML export and in-app Report preview.
 * Aligned with GSA openacr-editor report structure.
 */

export function isWcagStandard(standard) {
  if (!standard || typeof standard !== "object") return false;
  const blob = `${standard.label ?? ""} ${standard.name ?? ""} ${standard.id ?? ""}`.toLowerCase();
  return blob.includes("wcag") || blob.includes("web content accessibility guidelines");
}

export function mapChapterIdForExport(chapterId) {
  const id = chapterId == null ? "" : String(chapterId);
  if (id === "wcag_level_a") return "success_criteria_level_a";
  if (id === "wcag_level_aa") return "success_criteria_level_aa";
  if (id === "wcag_level_aaa") return "success_criteria_level_aaa";
  return id;
}

export function reportChapterHeadingLabel(mappedChapterId) {
  const map = {
    success_criteria_level_a: "Table 1: Success Criteria, Level A",
    success_criteria_level_aa: "Table 2: Success Criteria, Level AA",
    success_criteria_level_aaa: "Table 3: Success Criteria, Level AAA",
    functional_performance_criteria: "Chapter 3: Functional Performance Criteria (FPC)",
    hardware: "Chapter 4: Hardware",
    software: "Chapter 5: Software",
    support_documentation_and_services: "Chapter 6: Support Documentation and Services",
  };
  return map[mappedChapterId] || mappedChapterId;
}

export function compareVersionStrings(a, b) {
  const aParts = String(a || "0").split(".").map((v) => Number(v));
  const bParts = String(b || "0").split(".").map((v) => Number(v));
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i += 1) {
    const av = aParts[i] ?? 0;
    const bv = bParts[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

export function wcagVersionFromText(text) {
  const match = String(text || "").match(/wcag\s*([0-9]+(?:\.[0-9]+)?)/i);
  return match?.[1] || "";
}

export function reportHeadingForStandard(standard) {
  const label = String(standard?.label || "");
  if (isWcagStandard(standard)) {
    const version = wcagVersionFromText(label) || wcagVersionFromText(String(standard?.id || ""));
    return version ? `WCAG ${version} Report` : "WCAG Report";
  }
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("508")) return "Revised Section 508 Report";
  return standard?.label || "Report";
}

export function pickDisplayStandards(standards = []) {
  let highestWcag = null;
  let insertedWcag = false;
  return standards
    .reduce((result, standard) => {
      if (!isWcagStandard(standard)) {
        result.push(standard);
        return result;
      }
      const version = wcagVersionFromText(standard?.label) || wcagVersionFromText(standard?.id);
      if (!highestWcag || compareVersionStrings(version, highestWcag.version) > 0) {
        highestWcag = { standard, version };
      }
      if (!insertedWcag) {
        result.push(null);
        insertedWcag = true;
      }
      return result;
    }, [])
    .map((item) => (item === null ? highestWcag?.standard : item))
    .filter(Boolean);
}

export function getCatalogChapter(catalog, chapterId) {
  return (catalog?.chapters || []).find((chapter) => chapter.id === chapterId);
}

export function catalogCriteriaLabel(catalog, sourceChapterId, criteriaNum) {
  const chapter = getCatalogChapter(catalog, sourceChapterId);
  const item = (chapter?.criteria || []).find((c) => String(c.id) === String(criteriaNum));
  return item?.handle || "";
}

/** Same id slugging as Report preview table rows (must stay in sync with ReportCriteriaRow). */
export function toIdSegmentForReportRow(value) {
  return String(value ?? "").trim().replace(/[^a-zA-Z0-9_-]/g, "-");
}

/** DOM id for a criterion row on the in-app Report page (`<tr id="...">`). */
export function reportCriterionRowId(standardId, sourceChapterId, critId) {
  return `report-criteria-${toIdSegmentForReportRow(standardId)}-${toIdSegmentForReportRow(sourceChapterId)}-${toIdSegmentForReportRow(critId)}`;
}

/** Full guidance URL: criterion url or standard base + #alt_id (matches export HTML). */
export function criteriaGuidanceHref(catalog, sourceChapterId, criteriaNum, standardBaseUrl) {
  const chapter = getCatalogChapter(catalog, sourceChapterId);
  const item = (chapter?.criteria || []).find((c) => String(c.id) === String(criteriaNum));
  if (!item) return "";
  if (item.url) return String(item.url);
  const base = String(standardBaseUrl || "").trim();
  if (item.alt_id && base) return `${base}#${item.alt_id}`;
  return base;
}

export function componentLabelForReport(name) {
  const map = {
    web: "Web",
    "electronic-docs": "Electronic Documents",
    software: "Software",
    "authoring-tool": "Authoring Tool",
    none: "All",
  };
  return map[name] || name;
}

export function levelCountWithLevel(components = []) {
  return components.filter((component) => component?.adherence?.level).length;
}

export function isStandardChapterIncludedInReport(evaluation, sourceChapterId) {
  const ch = evaluation?.chapters?.[sourceChapterId];
  if (!ch) return false;
  return !ch.disabled || Boolean(String(ch.notes || "").trim());
}

export const DEFAULT_OPENACR_TERMS = [
  { id: "supports", label: "Supports", description: "The functionality of the product has at least one method that meets the criterion without known defects or meets with equivalent facilitation." },
  { id: "partially-supports", label: "Partially Supports", description: "Some functionality of the product does not meet the criterion." },
  { id: "does-not-support", label: "Does Not Support", description: "The majority of product functionality does not meet the criterion." },
  { id: "not-applicable", label: "Not Applicable", description: "The criterion is not relevant to the product." },
  { id: "not-evaluated", label: "Not Evaluated", description: "The product has not been evaluated against the criterion. This can be used only in WCAG 2.x Level AAA." },
];
